import { useEffect, useState, useRef, useCallback } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  Spin,
} from "antd";
import apiHelpers from "./data";
import { STORES, hashForLabel } from "../BlogsTable/stores";
import { triggerNotification } from "../../common/notify";

const STORE_OPTIONS = STORES.map((s) => ({
  value: s.hash,
  label: `${s.label} — ${s.name} (${s.hash})`,
}));

const FIELD_MAP = {
  store_hash: "store_hash",
  type_name: "type_name",
};

const emptyForm = (defaultStoreHash) => ({
  store_hash: defaultStoreHash || hashForLabel("UAT") || "",
  type_name: "",
});

const normalizeIncoming = (data) => ({
  store_hash: data?.storeHash || "",
  type_name: data?.typeName || "",
});

const diffPayload = (current, original) => {
  if (!original) return current;
  const patch = {};
  Object.keys(current).forEach((k) => {
    if (current[k] !== original[k]) patch[k] = current[k];
  });
  return patch;
};

const ProductTypeFormModal = ({
  open,
  mode,
  productTypeId,
  defaultStoreHash,
  onClose,
  onSaved,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [original, setOriginal] = useState(null);
  const [formDirty, setFormDirty] = useState(false);
  const abortRef = useRef(null);
  const initialValuesRef = useRef(null);

  const isCreate = mode === "create";
  const title = isCreate ? "New Product Type" : "Edit Product Type";

  const checkDirty = useCallback(() => {
    const current = JSON.stringify(form.getFieldsValue(true));
    setFormDirty(current !== initialValuesRef.current);
  }, [form]);

  useEffect(() => {
    if (!open) {
      if (abortRef.current) abortRef.current.abort();
      return;
    }

    if (isCreate || !productTypeId) {
      const initial = emptyForm(defaultStoreHash);
      form.resetFields();
      form.setFieldsValue(initial);
      setOriginal(null);
      initialValuesRef.current = JSON.stringify(form.getFieldsValue(true));
      setFormDirty(false);
      return;
    }

    const controller = new AbortController();
    abortRef.current = controller;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await apiHelpers.fetchProductTypeById(productTypeId, {
          signal: controller.signal,
        });
        if (cancelled || controller.signal.aborted) return;
        if (!data) {
          triggerNotification({
            type: "error",
            title: "Not found",
            message: "This product type may have been deleted.",
          });
          onClose?.();
          return;
        }
        const normalized = normalizeIncoming(data);
        form.resetFields();
        form.setFieldsValue(normalized);
        setOriginal(normalized);
        initialValuesRef.current = JSON.stringify(form.getFieldsValue(true));
        setFormDirty(false);
      } catch (err) {
        if (cancelled || controller.signal.aborted) return;
        const mapped = apiHelpers.mapError(err);
        if (mapped.code === "CANCELLED") return;
        triggerNotification({
          type: "error",
          title: "Failed to load product type",
          message: mapped.message,
        });
        onClose?.();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, productTypeId, mode]);

  const handleOk = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    const normalized = {
      store_hash: String(values.store_hash || "").trim(),
      type_name: String(values.type_name || "").trim(),
    };

    setSaving(true);
    try {
      let saved;
      if (isCreate) {
        saved = await apiHelpers.createProductType(normalized);
      } else {
        const patch = diffPayload(normalized, original);
        if (Object.keys(patch).length === 0) {
          triggerNotification({
            type: "info",
            title: "No changes",
            message: "Nothing to save.",
          });
          onClose?.();
          return;
        }
        saved = await apiHelpers.updateProductType(productTypeId, patch);
      }
      triggerNotification({
        type: "success",
        title: isCreate ? "Product type created" : "Product type updated",
        message: saved?.typeName || "Saved successfully.",
      });
      onSaved?.(saved);
    } catch (err) {
      const mapped = apiHelpers.mapError(err);
      if (mapped.code === "VALIDATION_ERROR" && mapped.fieldErrors.length) {
        const fields = mapped.fieldErrors
          .filter((f) => FIELD_MAP[f.field])
          .map((f) => ({ name: FIELD_MAP[f.field], errors: [f.message] }));
        if (fields.length) form.setFields(fields);
      } else if (mapped.code === "DUPLICATE_KEY") {
        form.setFields([
          {
            name: "type_name",
            errors: [
              "A product type with this name already exists for this store.",
            ],
          },
        ]);
      }
      triggerNotification({
        type: "error",
        title: isCreate ? "Create failed" : "Update failed",
        message: mapped.message,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      title={title}
      onCancel={onClose}
      onOk={handleOk}
      okText="Save"
      confirmLoading={saving}
      okButtonProps={{ disabled: !isCreate && !formDirty }}
      destroyOnClose
      maskClosable={false}
      width={520}
    >
      {loading ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin />
        </div>
      ) : (
        <Form form={form} layout="vertical" onValuesChange={checkDirty}>
          <Form.Item
            name="store_hash"
            label="Store"
            rules={[
              { required: true, message: "Store is required" },
              { max: 100, message: "Max 100 chars" },
            ]}
          >
            <Select
              showSearch
              options={STORE_OPTIONS}
              placeholder="Select a store"
              optionFilterProp="label"
            />
          </Form.Item>

          <Form.Item
            name="type_name"
            label={
              <span>
                Type name{" "}
                <span style={{ fontSize: 12, color: "rgba(0,0,0,0.45)" }}>
                  (unique per store)
                </span>
              </span>
            }
            rules={[
              { required: true, message: "Type name is required" },
              { max: 255, message: "Max 255 chars" },
              { pattern: /^[a-zA-Z0-9\s_\-.,!?'"''""&()/:;]+$/, message: "Special characters like @#%^*+=<>{}[]\\|~` are not allowed" },
              { pattern: /[a-zA-Z]/, message: "Must contain at least one letter. Only numbers not allowed." },
            ]}
          >
            <Input placeholder="e.g. Essential or Enhanced" autoFocus={isCreate} maxLength={255} />
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
};

export default ProductTypeFormModal;
